/**
 * Parse StatCrew schedule/roster artifact JSON for read-only table previews.
 * Field aliases mirror Statcrewuploadpage.tsx normalization.
 */

type ApiPayload = Record<string, unknown>;

function isApiPayload(value: unknown): value is ApiPayload {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toText(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function pickFirst(record: ApiPayload, keys: string[]): unknown {
  for (const key of keys) {
    const value = record[key];
    if (value !== null && value !== undefined) {
      return value;
    }
  }
  return undefined;
}

/** Pull array from artifact root (games / players / bare array). */
function extractRows(root: unknown, keys: string[]): unknown[] {
  if (Array.isArray(root)) return root;
  if (!isApiPayload(root)) return [];
  for (const key of keys) {
    const value = root[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function splitScore(rawScore: string): { ourScore: string; opponentScore: string } {
  const match = rawScore.match(/(-?\d+)\s*[-:–]\s*(-?\d+)/);
  if (!match) {
    return { ourScore: '', opponentScore: '' };
  }
  return { ourScore: match[1], opponentScore: match[2] };
}

function normalizeScheduleDate(value: string): string {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const slashDateMatch = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);
  if (slashDateMatch) {
    const [, month, day, rawYear] = slashDateMatch;
    const year = rawYear.length === 2 ? `20${rawYear}` : rawYear;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  const year = String(parsedDate.getFullYear());
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatMatchTypeForDisplay(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean).join(', ');
  }
  const text = String(value).trim();
  if (!text) return '';
  if (text.startsWith('[')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map((v) => String(v).trim()).filter(Boolean).join(', ');
      }
    } catch {
      return text;
    }
  }
  return text;
}

function normalizeClassYear(value: string): string {
  const normalized = value.trim().toLowerCase();

  if (normalized === 'fr' || normalized === 'freshman' || normalized === 'first year') return 'FR';
  if (normalized === 'so' || normalized === 'sophomore' || normalized === 'second year') return 'SO';
  if (normalized === 'jr' || normalized === 'junior' || normalized === 'third year') return 'JR';
  if (normalized === 'sr' || normalized === 'senior' || normalized === 'fourth year') return 'SR';

  return value;
}

export type SchedulePreviewRow = {
  date: string;
  opponent: string;
  ourScore: string;
  opponentScore: string;
  matchType: string;
};

export type RosterPreviewRow = {
  classYear: string;
  position: string;
  firstName: string;
  familyName: string;
};

export function parseScheduleArtifact(data: unknown): SchedulePreviewRow[] {
  const raw = extractRows(data, ['games', 'schedule', 'schedule_json']);

  return raw.map((row) => {
    if (!isApiPayload(row)) {
      return {
        date: '',
        opponent: '',
        ourScore: '',
        opponentScore: '',
        matchType: '',
      };
    }

    const scoreParts = splitScore(toText(pickFirst(row, ['score', 'gameScore'])));
    const ourScore =
      toText(pickFirst(row, ['ourScore', 'teamScore', 'scoreFor', 'our_score', 'team_score'])) || scoreParts.ourScore;
    const opponentScore =
      toText(pickFirst(row, ['opponentScore', 'awayScore', 'scoreAgainst', 'opponent_score', 'away_score'])) ||
      scoreParts.opponentScore;

    return {
      date: normalizeScheduleDate(toText(pickFirst(row, ['date', 'gameDate', 'matchDate', 'game_date']))),
      opponent: toText(pickFirst(row, ['opponent', 'opponentName', 'team', 'opponentTeam', 'opponent_name'])),
      ourScore,
      opponentScore,
      matchType: formatMatchTypeForDisplay(
        pickFirst(row, ['match_type', 'matchType', 'match_types'])
      ),
    };
  });
}

export function parseRosterArtifact(data: unknown): RosterPreviewRow[] {
  const raw = extractRows(data, ['players', 'roster', 'roster_json']);

  return raw.map((row) => {
    if (!isApiPayload(row)) {
      return {
        classYear: '',
        position: '',
        firstName: '',
        familyName: '',
      };
    }

    return {
      classYear: normalizeClassYear(toText(pickFirst(row, ['classYear', 'class', 'year', 'class_year']))),
      position: toText(pickFirst(row, ['position', 'pos', 'playerPosition', 'player_position'])),
      firstName: toText(pickFirst(row, ['firstName', 'first', 'givenName', 'first_name'])),
      familyName: toText(pickFirst(row, ['familyName', 'family_name', 'lastName', 'surname', 'last', 'last_name'])),
    };
  });
}

export function isErrorPayload(data: unknown): data is { error: string } {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof (data as { error: unknown }).error === 'string'
  );
}
